import { useState, useEffect, useCallback, useMemo } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { API } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Target, ChartLineUp, CalendarBlank, Plus, CheckCircle, TrendUp, TrendDown,
  Users, ListChecks, Trash, GearSix, Wrench, Briefcase, Megaphone, UserCircle,
  CaretLeft, CaretRight, MagnifyingGlass, X, PencilSimple, FloppyDisk, CalendarDots,
  FilePdf, FileXls, Wallet
} from "@phosphor-icons/react";
import BudgetModule from "@/components/kpi/BudgetModule";

const fmtPct = (n) => n != null ? `${(n * 100).toFixed(1)}%` : "—";
const fmtVal = (v, type) => {
  if (v == null || v === 0) return type === "money" ? "$0" : type === "hours" ? "0h" : "0";
  if (type === "money") return `$${Number(v).toLocaleString("es-MX", { minimumFractionDigits: 0 })}`;
  if (type === "hours") return `${Number(v).toFixed(1)}h`;
  if (type === "percentage") return `${Number(v).toFixed(1)}%`;
  return String(v);
};

const DEPT_LABELS = { ingeniero: "Ingeniería", comercial: "Comercial", marketing: "Marketing", auxiliar: "Auxiliar Adm.", admin: "Administración" };
const DEPT_ICONS = { ingeniero: Wrench, comercial: Briefcase, marketing: Megaphone, auxiliar: UserCircle, admin: Target };

const ACTIVITY_CATALOG = [
  { class: "Primarias", type: "A", phase: "Planeación", name: "Revisar metas y objetivos" },
  { class: "Primarias", type: "B", phase: "Planeación", name: "Planificar visitas/llamadas/reuniones" },
  { class: "Primarias", type: "C", phase: "Planeación", name: "Organizar agenda diaria" },
  { class: "Secundarias", type: "1", phase: "Prospección", name: "Clasificar leads" },
  { class: "Secundarias", type: "2", phase: "Prospección", name: "Llamadas de primer contacto" },
  { class: "Secundarias", type: "3", phase: "Prospección", name: "Agendar visitas/reuniones" },
  { class: "Secundarias", type: "4", phase: "Presentación", name: "Reunirse con clientes" },
  { class: "Secundarias", type: "5", phase: "Negociación", name: "Presentar propuesta/cotización" },
  { class: "Secundarias", type: "6", phase: "Negociación", name: "Cerrar cotizaciones" },
  { class: "Terciarias", type: "7", phase: "Postventa", name: "Confirmar satisfacción" },
  { class: "Terciarias", type: "8", phase: "Postventa", name: "Atender dudas/inconvenientes" },
  { class: "Terciarias", type: "9", phase: "Postventa", name: "Capacitación OnSite" },
  { class: "Terciarias", type: "10", phase: "Seguimiento", name: "Registrar actividades en CRM" },
  { class: "Terciarias", type: "11", phase: "Seguimiento", name: "Enviar reporte de avances" },
  { class: "Terciarias", type: "12", phase: "Reporte", name: "Analizar resultados" },
  { class: "Terciarias", type: "13", phase: "Reporte", name: "Proponer mejoras" },
];

const CLASS_BG = { Primarias: "bg-blue-50 border-blue-200", Secundarias: "bg-amber-50 border-amber-200", Terciarias: "bg-emerald-50 border-emerald-200" };
const PHASE_COLORS = { Planeación: "bg-blue-500", Prospección: "bg-amber-500", Presentación: "bg-orange-500", Negociación: "bg-red-500", Postventa: "bg-emerald-500", Seguimiento: "bg-cyan-500", Reporte: "bg-purple-500" };

export default function KPIModule() {
  const { user } = useAuth();
  const [tab, setTab] = useState("my-kpis");
  const isAdmin = user?.role === "admin";
  const tabs = [
    { id: "my-kpis", label: "Mis KPIs", icon: ChartLineUp },
    { id: "activities", label: "Actividades", icon: ListChecks },
    { id: "calendar", label: "Agenda", icon: CalendarDots },
    ...((isAdmin || user?.role === "comercial" || user?.role === "auxiliar" || user?.role === "subgerente") ? [{ id: "presupuestos", label: "Presupuestos", icon: Wallet }] : []),
    ...(isAdmin ? [{ id: "config", label: "Configurar", icon: GearSix }] : []),
  ];

  return (
    <div className="space-y-5" data-testid="kpi-module">
      <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2"><Target size={28} weight="fill" className="text-indigo-600" /> KPIs y Objetivos</h1>
      <div className="flex gap-1 bg-slate-100 p-1 rounded-lg w-fit">
        {tabs.map(t => (
          <button key={t.id} onClick={() => setTab(t.id)}
            className={`px-4 py-2 text-sm font-medium rounded-md transition-colors flex items-center gap-1.5 ${tab === t.id ? "bg-white text-slate-900 shadow-sm" : "text-slate-500"}`}
            data-testid={`kpi-tab-${t.id}`}>
            <t.icon size={16} /> {t.label}
          </button>
        ))}
      </div>
      {tab === "my-kpis" && <MyKPIsDashboard />}
      {tab === "activities" && <ActivitiesPanel />}
      {tab === "calendar" && <CalendarPanel />}
      {tab === "presupuestos" && <BudgetModule />}
      {tab === "config" && isAdmin && <KPIConfigPanel />}
    </div>
  );
}

// =================== MY KPIs DASHBOARD ===================
function MyKPIsDashboard() {
  const { token, user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editingCell, setEditingCell] = useState(null);
  const [editValue, setEditValue] = useState("");
  const [selMonth, setSelMonth] = useState(new Date().getMonth() + 1);
  const [selYear, setSelYear] = useState(new Date().getFullYear());
  const headers = { Authorization: `Bearer ${token}` };
  const ML = ["Enero","Febrero","Marzo","Abril","Mayo","Junio","Julio","Agosto","Septiembre","Octubre","Noviembre","Diciembre"];

  const loadData = useCallback(() => {
    setLoading(true);
    axios.get(`${API}/kpi/my-dashboard?month=${selMonth}&year=${selYear}`, { headers })
      .then(r => setData(r.data))
      .catch(e => process.env.NODE_ENV === "development" && console.error(e.message))
      .finally(() => setLoading(false));
  }, [token, selMonth, selYear]);
  useEffect(() => { loadData(); }, [loadData]);

  const isAdm = data?.is_admin;
  const period = `${selYear}-${String(selMonth).padStart(2, "0")}`;

  const handleSaveMeta = async (tid, w) => {
    const v = parseFloat(editValue); if (isNaN(v)) { setEditingCell(null); return; }
    try { await axios.post(`${API}/kpi/weekly-meta`, { template_id: tid, week: w, meta: v, period, target_user_id: user?.id }, { headers }); setEditingCell(null); loadData(); }
    catch { toast.error("Error"); }
  };
  const handleSaveReal = async (tid, w) => {
    const v = parseFloat(editValue); if (isNaN(v)) { setEditingCell(null); return; }
    try { await axios.post(`${API}/kpi/weekly-real`, { template_id: tid, week: w, value: v, period }, { headers }); setEditingCell(null); loadData(); }
    catch { toast.error("Error"); }
  };
  const handleExport = async (type) => {
    try {
      const res = await axios.get(`${API}/kpi/export/${type}?month=${selMonth}&year=${selYear}`, { headers, responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a"); a.href = url; a.download = `kpis_${selMonth}_${selYear}.${type === "pdf" ? "pdf" : "xlsx"}`;
      document.body.appendChild(a); a.click(); a.remove(); toast.success(`${type.toUpperCase()} descargado`);
    } catch { toast.error("Error al exportar"); }
  };

  if (loading) return <div className="text-center py-12 text-slate-400">Calculando KPIs...</div>;
  if (!data || data.templates.length === 0) return (
    <div className="text-center py-16 bg-white rounded-xl border">
      <Target size={48} className="mx-auto text-slate-300 mb-3" />
      <p className="text-slate-500 font-medium">Sin KPIs configurados para tu departamento</p>
      <p className="text-sm text-slate-400 mt-1">El administrador debe configurar los objetivos en "Configurar"</p>
    </div>
  );

  const WEEKS = [1, 2, 3, 4, 5];

  return (
    <div className="space-y-5">
      {/* Month Selector + Export */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-2">
          <span className="text-sm text-slate-500">{DEPT_LABELS[data.department] || data.department}</span>
          <select value={selMonth} onChange={e => setSelMonth(parseInt(e.target.value))} className="border rounded-lg px-2 py-1.5 text-sm" data-testid="kpi-month-select">
            {ML.map((m, i) => <option key={i} value={i + 1}>{m}</option>)}
          </select>
          <select value={selYear} onChange={e => setSelYear(parseInt(e.target.value))} className="border rounded-lg px-2 py-1.5 text-sm" data-testid="kpi-year-select">
            {[2025, 2026, 2027].map(y => <option key={y} value={y}>{y}</option>)}
          </select>
          {data.current_week > 0 && <span className="text-xs text-indigo-600 font-medium bg-indigo-50 px-2 py-1 rounded-full">Semana {data.current_week}</span>}
        </div>
        <div className="flex gap-2">
          <button onClick={() => handleExport("pdf")} className="px-3 py-1.5 bg-red-50 text-red-700 rounded-lg text-xs font-medium hover:bg-red-100 flex items-center gap-1" data-testid="kpi-export-pdf"><FilePdf size={14} /> PDF</button>
          <button onClick={() => handleExport("excel")} className="px-3 py-1.5 bg-emerald-50 text-emerald-700 rounded-lg text-xs font-medium hover:bg-emerald-100 flex items-center gap-1" data-testid="kpi-export-excel"><FileXls size={14} /> Excel</button>
        </div>
      </div>
      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
        {data.templates.map(tmpl => {
          const m = data.month[tmpl.id] || {}; const pct = m.pct || 0;
          const isGood = pct >= 0.8; const isBad = pct < 0.5 && m.meta > 0;
          return (
            <div key={tmpl.id} className={`rounded-xl border p-3 ${isGood ? "bg-emerald-50 border-emerald-200" : isBad ? "bg-red-50 border-red-200" : "bg-white"}`}>
              <p className="text-[9px] font-semibold text-slate-500 uppercase tracking-wider line-clamp-1">{tmpl.name}</p>
              <div className="flex items-end justify-between mt-1">
                <p className={`text-xl font-black ${isGood ? "text-emerald-700" : isBad ? "text-red-700" : "text-slate-800"}`}>{fmtVal(m.real, tmpl.value_type)}</p>
                <div className="text-right">
                  <p className="text-[10px] text-slate-400">Meta: {fmtVal(m.meta, tmpl.value_type)}</p>
                  {m.meta > 0 && <p className={`text-xs font-bold ${isGood ? "text-emerald-600" : isBad ? "text-red-600" : "text-amber-600"}`}>{pct >= 0.8 ? <TrendUp size={10} className="inline mr-0.5" /> : <TrendDown size={10} className="inline mr-0.5" />}{fmtPct(pct)}</p>}
                </div>
              </div>
              <div className="w-full h-1.5 bg-slate-100 rounded-full mt-2 overflow-hidden">
                <div className={`h-full rounded-full ${isGood ? "bg-emerald-500" : isBad ? "bg-red-500" : "bg-amber-400"}`} style={{ width: `${Math.min(pct * 100, 100)}%` }} />
              </div>
            </div>
          );
        })}
      </div>
      {/* Weekly Table */}
      <div className="bg-white rounded-xl border overflow-hidden">
        <div className="bg-indigo-600 px-5 py-3 flex items-center justify-between">
          <h3 className="text-white font-bold flex items-center gap-2"><CalendarBlank size={18} /> Objetivos Semanales — {data.month_name} {data.year}</h3>
          <span className="text-white/60 text-[10px]">{isAdm ? "Admin: edita META y REAL" : "Edita REAL. Metas definidas por admin 🔒"}</span>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-xs" data-testid="kpi-weekly-table">
            <thead>
              <tr className="bg-slate-50 border-b">
                <th className="px-3 py-2.5 text-left text-[10px] font-semibold text-slate-500 min-w-[130px]">OBJETIVO</th>
                {WEEKS.map(w => <th key={w} colSpan={3} className={`px-1 py-2.5 text-center text-[10px] font-semibold ${w === data.current_week ? "bg-indigo-50 text-indigo-700" : "text-slate-500"}`}>SEM {w}{w === data.current_week ? " ●" : ""}</th>)}
                <th colSpan={3} className="px-1 py-2.5 text-center text-[10px] font-semibold text-slate-700 bg-slate-100">TOTAL MES</th>
              </tr>
              <tr className="border-b"><th></th>
                {[...WEEKS, "t"].map(w => [
                  <th key={`${w}m`} className="px-1 py-1 text-center text-[8px] text-slate-400">{isAdm ? "META" : "META 🔒"}</th>,
                  <th key={`${w}r`} className="px-1 py-1 text-center text-[8px] text-blue-500 font-semibold">REAL</th>,
                  <th key={`${w}p`} className="px-1 py-1 text-center text-[8px] text-slate-400">%</th>
                ])}
              </tr>
            </thead>
            <tbody className="divide-y">
              {data.templates.map(tmpl => {
                const mo = data.month[tmpl.id] || {};
                return (
                  <tr key={tmpl.id} className="hover:bg-slate-50">
                    <td className="px-3 py-2 font-medium text-slate-700 text-xs">{tmpl.name}</td>
                    {WEEKS.map(w => {
                      const wd = (data.weeks[`week_${w}`] || {})[tmpl.id] || {};
                      const pct = wd.pct || 0;
                      const eMeta = editingCell?.id === tmpl.id && editingCell?.week === w && editingCell?.type === "meta";
                      const eReal = editingCell?.id === tmpl.id && editingCell?.week === w && editingCell?.type === "real";
                      return [
                        <td key={`${w}m`} className="px-1 py-1.5 text-center">{isAdm ? (eMeta ? (
                          <input type="number" value={editValue} onChange={e => setEditValue(e.target.value)}
                            onKeyDown={e => { if (e.key === "Enter") handleSaveMeta(tmpl.id, w); if (e.key === "Escape") setEditingCell(null); }}
                            onBlur={() => handleSaveMeta(tmpl.id, w)} className="w-14 h-6 text-[10px] border border-indigo-300 rounded px-1 text-center bg-indigo-50" autoFocus />
                        ) : (
                          <button onClick={() => { setEditingCell({ id: tmpl.id, week: w, type: "meta" }); setEditValue(String(wd.meta || 0)); }}
                            className="text-slate-500 hover:text-indigo-600 hover:bg-indigo-50 px-1 py-0.5 rounded cursor-pointer text-[10px]">{fmtVal(wd.meta, tmpl.value_type)}</button>
                        )) : <span className="text-[10px] text-slate-400">{fmtVal(wd.meta, tmpl.value_type)}</span>}</td>,
                        <td key={`${w}r`} className="px-1 py-1.5 text-center">{eReal ? (
                          <input type="number" value={editValue} onChange={e => setEditValue(e.target.value)}
                            onKeyDown={e => { if (e.key === "Enter") handleSaveReal(tmpl.id, w); if (e.key === "Escape") setEditingCell(null); }}
                            onBlur={() => handleSaveReal(tmpl.id, w)} className="w-14 h-6 text-[10px] border border-blue-300 rounded px-1 text-center bg-blue-50" autoFocus />
                        ) : (
                          <button onClick={() => { setEditingCell({ id: tmpl.id, week: w, type: "real" }); setEditValue(String(wd.real || 0)); }}
                            className="font-semibold text-slate-800 hover:text-blue-600 hover:bg-blue-50 px-1 py-0.5 rounded cursor-pointer text-[10px]">{fmtVal(wd.real, tmpl.value_type)}</button>
                        )}</td>,
                        <td key={`${w}p`} className={`px-1 py-2 text-center font-bold text-[10px] ${pct >= 0.8 ? "text-emerald-600" : pct >= 0.5 ? "text-amber-600" : pct > 0 ? "text-red-600" : "text-slate-300"}`}>{wd.meta > 0 ? fmtPct(pct) : "—"}</td>,
                      ];
                    })}
                    <td className="px-1 py-2 text-center text-slate-600 bg-slate-50 font-medium text-[10px]">{fmtVal(mo.meta, tmpl.value_type)}</td>
                    <td className="px-1 py-2 text-center font-bold text-slate-900 bg-slate-50 text-[10px]">{fmtVal(mo.real, tmpl.value_type)}</td>
                    <td className={`px-1 py-2 text-center font-black bg-slate-50 text-[10px] ${(mo.pct||0) >= 0.8 ? "text-emerald-600" : (mo.pct||0) >= 0.5 ? "text-amber-600" : (mo.pct||0) > 0 ? "text-red-600" : "text-slate-300"}`}>{mo.meta > 0 ? fmtPct(mo.pct) : "—"}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

// =================== ACTIVITIES PANEL (with contacts) ===================
function ActivitiesPanel() {
  const { token, user } = useAuth();
  const canDelete = user?.role !== "subgerente";
  const [activities, setActivities] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ catalog_type: "", description: "", date: new Date().toISOString().slice(0, 10), contact_id: "", contact_name: "", contact_company: "" });
  const [weekFilter, setWeekFilter] = useState("all");
  const [contactSearch, setContactSearch] = useState("");
  const [contactResults, setContactResults] = useState([]);
  const [showContactCreate, setShowContactCreate] = useState(false);
  const [newContact, setNewContact] = useState({ name: "", company: "", phone: "", email: "" });
  const headers = { Authorization: `Bearer ${token}` };

  const load = useCallback(async () => {
    try { const { data } = await axios.get(`${API}/kpi/activities`, { headers }); setActivities(data || []); }
    catch (e) { process.env.NODE_ENV === "development" && console.error(e.message); }
  }, [token]);
  useEffect(() => { load(); }, [load]);

  const searchContacts = async (q) => {
    setContactSearch(q);
    if (q.length < 2) { setContactResults([]); return; }
    try { const { data } = await axios.get(`${API}/kpi/contacts/search?q=${encodeURIComponent(q)}`, { headers }); setContactResults(data); }
    catch { setContactResults([]); }
  };

  const selectContact = (c) => {
    setForm({ ...form, contact_id: c.id, contact_name: c.name, contact_company: c.company });
    setContactSearch("");
    setContactResults([]);
  };

  const handleCreateContact = async () => {
    if (!newContact.name.trim()) { toast.error("Nombre requerido"); return; }
    try {
      const { data } = await axios.post(`${API}/kpi/contacts`, newContact, { headers });
      selectContact(data);
      setShowContactCreate(false);
      setNewContact({ name: "", company: "", phone: "", email: "" });
      toast.success("Contacto creado");
    } catch { toast.error("Error"); }
  };

  const handleCreate = async () => {
    if (!form.description.trim()) { toast.error("Descripción requerida"); return; }
    const cat = ACTIVITY_CATALOG.find(a => a.type === form.catalog_type);
    try {
      await axios.post(`${API}/kpi/activities`, { ...form, catalog_class: cat?.class || "", catalog_phase: cat?.phase || "", catalog_name: cat?.name || "" }, { headers });
      toast.success("Actividad registrada");
      setForm({ catalog_type: "", description: "", date: new Date().toISOString().slice(0, 10), contact_id: "", contact_name: "", contact_company: "" });
      setShowForm(false); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };

  const handleToggle = async (id, status) => { await axios.put(`${API}/kpi/activities/${id}`, { status: status === "completada" ? "pendiente" : "completada" }, { headers }); load(); };
  const handleDelete = async (id) => { await axios.delete(`${API}/kpi/activities/${id}`, { headers }); load(); };

  const filtered = activities.filter(a => {
    if (weekFilter === "all") return true;
    const d = new Date(a.date); return Math.min(Math.ceil(d.getDate() / 7), 4) === parseInt(weekFilter);
  });
  const completed = filtered.filter(a => a.status === "completada").length;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex gap-1.5">
          {["all", "1", "2", "3", "4"].map(w => (
            <button key={w} onClick={() => setWeekFilter(w)} className={`px-3 py-1.5 text-xs font-medium rounded-lg ${weekFilter === w ? "bg-indigo-600 text-white" : "bg-slate-100 text-slate-600"}`}>{w === "all" ? "Todo" : `Sem ${w}`}</button>
          ))}
        </div>
        <Button size="sm" onClick={() => setShowForm(!showForm)} data-testid="kpi-new-activity"><Plus size={14} className="mr-1" /> Nueva Actividad</Button>
      </div>
      <div className="grid grid-cols-3 gap-3">
        <div className="bg-slate-50 border rounded-xl p-3 text-center"><p className="text-2xl font-black text-slate-800">{filtered.length}</p><p className="text-[10px] text-slate-500">Total</p></div>
        <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-3 text-center"><p className="text-2xl font-black text-emerald-700">{completed}</p><p className="text-[10px] text-emerald-600">Completadas</p></div>
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 text-center"><p className="text-2xl font-black text-amber-700">{filtered.length - completed}</p><p className="text-[10px] text-amber-600">Pendientes</p></div>
      </div>

      {showForm && (
        <div className="bg-slate-50 border rounded-xl p-4 space-y-3" data-testid="kpi-activity-form">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div><Label className="text-xs">Tipo de Actividad</Label>
              <Select value={form.catalog_type} onValueChange={v => setForm({ ...form, catalog_type: v })}>
                <SelectTrigger className="mt-1 h-9 text-xs"><SelectValue placeholder="Seleccionar..." /></SelectTrigger>
                <SelectContent>{ACTIVITY_CATALOG.map(a => <SelectItem key={a.type} value={a.type}><span className="text-xs">[{a.type}] {a.name}</span></SelectItem>)}</SelectContent>
              </Select>
            </div>
            {/* Contact Search */}
            <div className="relative">
              <Label className="text-xs">Contacto / Cliente</Label>
              {form.contact_id ? (
                <div className="mt-1 flex items-center gap-1.5 bg-blue-50 border border-blue-200 rounded-lg px-3 py-1.5 h-9">
                  <span className="text-xs font-medium text-blue-700 flex-1 truncate">{form.contact_name}{form.contact_company ? ` — ${form.contact_company}` : ""}</span>
                  <button onClick={() => setForm({ ...form, contact_id: "", contact_name: "", contact_company: "" })} className="text-blue-400 hover:text-blue-600"><X size={14} /></button>
                </div>
              ) : (
                <>
                  <div className="relative mt-1">
                    <MagnifyingGlass size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
                    <input value={contactSearch} onChange={e => searchContacts(e.target.value)} placeholder="Buscar contacto..." className="w-full pl-8 pr-16 h-9 border rounded-lg text-xs" data-testid="kpi-contact-search" />
                    <button onClick={() => setShowContactCreate(true)} className="absolute right-1 top-1/2 -translate-y-1/2 text-[10px] font-medium text-blue-600 bg-blue-50 px-2 py-1 rounded hover:bg-blue-100" data-testid="kpi-create-contact-btn">+ Crear</button>
                  </div>
                  {contactResults.length > 0 && (
                    <div className="absolute z-20 mt-1 w-full bg-white border rounded-lg shadow-lg max-h-40 overflow-y-auto">
                      {contactResults.map(c => (
                        <button key={c.id} onClick={() => selectContact(c)} className="w-full text-left px-3 py-2 hover:bg-blue-50 text-xs border-b last:border-0">
                          <span className="font-medium text-slate-800">{c.name}</span>
                          {c.company && <span className="text-slate-400 ml-1">— {c.company}</span>}
                          <span className={`ml-2 text-[9px] px-1.5 py-0.5 rounded-full ${c.source === "crm" ? "bg-blue-100 text-blue-600" : "bg-slate-100 text-slate-500"}`}>{c.source === "crm" ? "CRM" : "Cotización"}</span>
                        </button>
                      ))}
                    </div>
                  )}
                </>
              )}
            </div>
            <div><Label className="text-xs">Fecha</Label><Input type="date" value={form.date} onChange={e => setForm({ ...form, date: e.target.value })} className="mt-1 h-9 text-sm" /></div>
          </div>
          {showContactCreate && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 space-y-2">
              <p className="text-xs font-semibold text-blue-700">Crear Contacto Rápido</p>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                <Input value={newContact.name} onChange={e => setNewContact({ ...newContact, name: e.target.value })} placeholder="Nombre *" className="h-8 text-xs" data-testid="kpi-new-contact-name" />
                <Input value={newContact.company} onChange={e => setNewContact({ ...newContact, company: e.target.value })} placeholder="Empresa" className="h-8 text-xs" />
                <Input value={newContact.phone} onChange={e => setNewContact({ ...newContact, phone: e.target.value })} placeholder="Teléfono" className="h-8 text-xs" />
                <div className="flex gap-1">
                  <Button size="sm" className="h-8 text-xs" onClick={handleCreateContact} data-testid="kpi-save-contact">Crear</Button>
                  <Button size="sm" variant="outline" className="h-8 text-xs" onClick={() => setShowContactCreate(false)}>X</Button>
                </div>
              </div>
            </div>
          )}
          <div><Label className="text-xs">Descripción *</Label><Textarea value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} className="mt-1 text-sm min-h-[60px]" placeholder="Describe la actividad..." data-testid="kpi-activity-desc" /></div>
          <div className="flex gap-2"><Button size="sm" onClick={handleCreate} data-testid="kpi-save-activity">Registrar</Button><Button size="sm" variant="outline" onClick={() => setShowForm(false)}>Cancelar</Button></div>
        </div>
      )}

      <div className="space-y-2" data-testid="kpi-activities-list">
        {filtered.length === 0 ? <p className="text-center text-sm text-slate-400 py-8">Sin actividades. Clic "Nueva Actividad".</p> :
          filtered.map(a => {
            const bg = CLASS_BG[a.catalog_class] || "bg-slate-50 border-slate-200";
            return (
              <div key={a.id} className={`border rounded-lg p-3 flex items-start gap-3 ${bg}`} data-testid={`kpi-activity-${a.id}`}>
                <button onClick={() => handleToggle(a.id, a.status)} className={`mt-0.5 shrink-0 ${a.status === "completada" ? "text-emerald-500" : "text-slate-300"}`}>
                  <CheckCircle size={22} weight={a.status === "completada" ? "fill" : "regular"} />
                </button>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-[9px] font-bold px-2 py-0.5 rounded-full bg-white/60 border">{a.catalog_class || "General"} [{a.catalog_type || "-"}]</span>
                    <span className="text-[9px] text-slate-400">{a.catalog_phase}</span>
                    {a.contact_name && (
                      <span className="text-[9px] bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full font-medium">{a.contact_name}{a.contact_company ? ` — ${a.contact_company}` : ""}</span>
                    )}
                  </div>
                  <p className={`text-sm mt-0.5 ${a.status === "completada" ? "line-through text-slate-400" : "text-slate-800 font-medium"}`}>{a.description}</p>
                  <div className="flex items-center gap-3 text-[10px] text-slate-400 mt-1">
                    <span className="font-mono">{a.date}</span><span>{a.user_name}</span>
                  </div>
                </div>
                <button onClick={() => handleDelete(a.id)} className="text-slate-300 hover:text-red-500 shrink-0" style={{ display: canDelete ? undefined : "none" }}><Trash size={14} /></button>
              </div>
            );
          })}
      </div>
    </div>
  );
}

// =================== CALENDAR PANEL ===================
function CalendarPanel() {
  const { token } = useAuth();
  const [calData, setCalData] = useState(null);
  const [viewMonth, setViewMonth] = useState(new Date().getMonth() + 1);
  const [viewYear, setViewYear] = useState(new Date().getFullYear());
  const [selectedDay, setSelectedDay] = useState(null);
  const headers = { Authorization: `Bearer ${token}` };

  const MONTHS = ["Enero","Febrero","Marzo","Abril","Mayo","Junio","Julio","Agosto","Septiembre","Octubre","Noviembre","Diciembre"];
  const DAYS = ["Lun", "Mar", "Mié", "Jue", "Vie", "Sáb", "Dom"];

  useEffect(() => {
    axios.get(`${API}/kpi/calendar?month=${viewMonth}&year=${viewYear}`, { headers })
      .then(r => setCalData(r.data))
      .catch(e => process.env.NODE_ENV === "development" && console.error(e.message));
  }, [token, viewMonth, viewYear]);

  const prevMonth = () => { if (viewMonth === 1) { setViewMonth(12); setViewYear(viewYear - 1); } else setViewMonth(viewMonth - 1); setSelectedDay(null); };
  const nextMonth = () => { if (viewMonth === 12) { setViewMonth(1); setViewYear(viewYear + 1); } else setViewMonth(viewMonth + 1); setSelectedDay(null); };

  const calendarDays = useMemo(() => {
    const firstDay = new Date(viewYear, viewMonth - 1, 1);
    const lastDay = new Date(viewYear, viewMonth, 0);
    const startDow = (firstDay.getDay() + 6) % 7; // Mon=0
    const days = [];
    for (let i = 0; i < startDow; i++) days.push(null);
    for (let d = 1; d <= lastDay.getDate(); d++) days.push(d);
    return days;
  }, [viewMonth, viewYear]);

  const activitiesByDay = useMemo(() => {
    if (!calData?.activities) return {};
    const map = {};
    for (const a of calData.activities) {
      const day = parseInt(a.date?.slice(8, 10));
      if (!map[day]) map[day] = [];
      map[day].push(a);
    }
    return map;
  }, [calData]);

  const today = new Date();
  const isToday = (d) => d && viewYear === today.getFullYear() && viewMonth === today.getMonth() + 1 && d === today.getDate();
  const dayActivities = selectedDay ? (activitiesByDay[selectedDay] || []) : [];

  return (
    <div className="space-y-4">
      {/* Phase Legend */}
      <div className="flex items-center gap-3 flex-wrap">
        {Object.entries(PHASE_COLORS).map(([phase, color]) => (
          <div key={phase} className="flex items-center gap-1.5">
            <div className={`w-2.5 h-2.5 rounded-full ${color}`} />
            <span className="text-[10px] text-slate-500">{phase}</span>
          </div>
        ))}
      </div>

      <div className="bg-white rounded-xl border overflow-hidden" data-testid="kpi-calendar">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-3 bg-indigo-600">
          <button onClick={prevMonth} className="text-white/80 hover:text-white"><CaretLeft size={20} /></button>
          <h3 className="text-white font-bold text-lg">{MONTHS[viewMonth - 1]} {viewYear}</h3>
          <button onClick={nextMonth} className="text-white/80 hover:text-white"><CaretRight size={20} /></button>
        </div>

        {/* Days header */}
        <div className="grid grid-cols-7 border-b">
          {DAYS.map(d => <div key={d} className="py-2 text-center text-[10px] font-semibold text-slate-500 bg-slate-50">{d}</div>)}
        </div>

        {/* Calendar Grid */}
        <div className="grid grid-cols-7">
          {calendarDays.map((day, i) => {
            const acts = day ? (activitiesByDay[day] || []) : [];
            const isSel = day === selectedDay;
            return (
              <div key={i} onClick={() => day && setSelectedDay(isSel ? null : day)}
                className={`min-h-[80px] p-1.5 border-b border-r cursor-pointer transition-colors ${!day ? "bg-slate-50" : isSel ? "bg-indigo-50" : "hover:bg-slate-50"} ${isToday(day) ? "ring-2 ring-inset ring-indigo-400" : ""}`}
                data-testid={day ? `cal-day-${day}` : undefined}>
                {day && (
                  <>
                    <span className={`text-xs font-medium ${isToday(day) ? "text-indigo-700 font-bold" : "text-slate-600"}`}>{day}</span>
                    <div className="mt-1 space-y-0.5">
                      {acts.slice(0, 3).map(a => {
                        const pc = PHASE_COLORS[a.catalog_phase] || "bg-slate-400";
                        return (
                          <div key={a.id} className="flex items-center gap-1">
                            <div className={`w-1.5 h-1.5 rounded-full shrink-0 ${pc}`} />
                            <span className="text-[8px] text-slate-600 truncate">{a.description?.substring(0, 18)}</span>
                          </div>
                        );
                      })}
                      {acts.length > 3 && <span className="text-[8px] text-indigo-500 font-medium">+{acts.length - 3} más</span>}
                    </div>
                  </>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Selected Day Detail */}
      {selectedDay && (
        <div className="bg-white rounded-xl border p-4" data-testid="cal-day-detail">
          <h4 className="font-semibold text-slate-700 mb-3">{selectedDay} de {MONTHS[viewMonth - 1]} — {dayActivities.length} actividad(es)</h4>
          {dayActivities.length === 0 ? <p className="text-sm text-slate-400">Sin actividades este día</p> : (
            <div className="space-y-2">
              {dayActivities.map(a => {
                const pc = PHASE_COLORS[a.catalog_phase] || "bg-slate-400";
                return (
                  <div key={a.id} className="flex items-start gap-3 p-2.5 border rounded-lg">
                    <div className={`w-3 h-3 rounded-full mt-1 shrink-0 ${pc}`} />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded-full ${a.status === "completada" ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}`}>{a.status === "completada" ? "Completada" : "Pendiente"}</span>
                        <span className="text-[9px] text-slate-400">{a.catalog_phase}</span>
                        {a.contact_name && <span className="text-[9px] bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded-full">{a.contact_name}</span>}
                      </div>
                      <p className="text-sm text-slate-800 font-medium mt-0.5">{a.description}</p>
                      <span className="text-[10px] text-slate-400">{a.user_name}</span>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// =================== KPI CONFIG PANEL (Admin) ===================
function KPIConfigPanel() {
  const { token } = useAuth();
  const [selectedDept, setSelectedDept] = useState("comercial");
  const [templates, setTemplates] = useState([]);
  const [dataSources, setDataSources] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({ name: "", data_source: "manual", value_type: "number", default_meta: "" });
  const headers = { Authorization: `Bearer ${token}` };

  const load = useCallback(async () => {
    try {
      const [tRes, dsRes] = await Promise.all([
        axios.get(`${API}/kpi/templates/${selectedDept}`, { headers }),
        axios.get(`${API}/kpi/data-sources`, { headers }),
      ]);
      setTemplates(tRes.data || []);
      setDataSources(dsRes.data || []);
    } catch (e) { process.env.NODE_ENV === "development" && console.error(e.message); }
  }, [token, selectedDept]);
  useEffect(() => { load(); }, [load]);

  const resetForm = () => {
    setForm({ name: "", data_source: "manual", value_type: "number", default_meta: "" });
    setEditingId(null);
    setShowForm(false);
  };

  const handleStartEdit = (t) => {
    setEditingId(t.id);
    setForm({ name: t.name, data_source: t.data_source, value_type: t.value_type, default_meta: String(t.default_meta || "") });
    setShowForm(true);
  };

  const handleSave = async () => {
    if (!form.name.trim()) { toast.error("Nombre requerido"); return; }
    try {
      if (editingId) {
        await axios.put(`${API}/kpi/templates/${editingId}`, { ...form, default_meta: parseFloat(form.default_meta) || 0 }, { headers });
        toast.success("Objetivo actualizado");
      } else {
        await axios.post(`${API}/kpi/templates`, { ...form, department: selectedDept, default_meta: parseFloat(form.default_meta) || 0 }, { headers });
        toast.success("Objetivo creado");
      }
      resetForm(); load();
    } catch { toast.error("Error"); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Eliminar este objetivo KPI?")) return;
    try { await axios.delete(`${API}/kpi/templates/${id}`, { headers }); toast.success("Eliminado"); load(); } catch { toast.error("Error"); }
  };

  const depts = ["comercial", "ingeniero", "marketing", "auxiliar", "admin"];
  const typeLabels = { number: "Número", money: "Dinero", percentage: "%", hours: "Horas" };

  return (
    <div className="space-y-4" data-testid="kpi-config">
      <div className="flex gap-3">
        <div className="w-52 space-y-1">
          <p className="text-xs font-semibold text-slate-500 mb-2 uppercase tracking-wider">Departamentos</p>
          {depts.map(d => {
            const Icon = DEPT_ICONS[d] || Target;
            return (
              <button key={d} onClick={() => { setSelectedDept(d); resetForm(); }}
                className={`w-full flex items-center gap-2 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${selectedDept === d ? "bg-indigo-600 text-white" : "bg-white border text-slate-700 hover:bg-slate-50"}`}
                data-testid={`kpi-dept-${d}`}><Icon size={16} /> {DEPT_LABELS[d] || d}</button>
            );
          })}
        </div>
        <div className="flex-1">
          <div className="bg-white rounded-xl border overflow-hidden">
            <div className="bg-indigo-600 px-5 py-3 flex items-center justify-between">
              <h3 className="text-white font-bold flex items-center gap-2"><Target size={18} /> Objetivos KPI — {DEPT_LABELS[selectedDept]}</h3>
              <button onClick={() => { resetForm(); setShowForm(true); }} className="text-white/80 hover:text-white text-xs font-medium bg-white/20 px-3 py-1 rounded-full" data-testid="kpi-add-template"><Plus size={12} className="inline mr-1" /> Agregar</button>
            </div>
            {showForm && (
              <div className="p-4 border-b bg-indigo-50 space-y-3" data-testid="kpi-template-form">
                <p className="text-xs font-semibold text-indigo-700">{editingId ? "Editar Objetivo" : "Nuevo Objetivo"}</p>
                <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                  <div><Label className="text-xs">Nombre *</Label><Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} className="mt-1 h-9 text-sm" placeholder="Ej: Tickets Resueltos" data-testid="kpi-template-name" /></div>
                  <div><Label className="text-xs">Fuente de Datos</Label>
                    <Select value={form.data_source} onValueChange={v => setForm({ ...form, data_source: v })}>
                      <SelectTrigger className="mt-1 h-9 text-xs"><SelectValue /></SelectTrigger>
                      <SelectContent>{dataSources.map(ds => <SelectItem key={ds.key} value={ds.key}><span className="text-xs">{ds.label}</span></SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div><Label className="text-xs">Tipo</Label>
                    <Select value={form.value_type} onValueChange={v => setForm({ ...form, value_type: v })}>
                      <SelectTrigger className="mt-1 h-9 text-xs"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="number">Número</SelectItem><SelectItem value="money">Dinero ($)</SelectItem>
                        <SelectItem value="percentage">Porcentaje (%)</SelectItem><SelectItem value="hours">Horas (h)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div><Label className="text-xs">Meta Default</Label><Input type="number" value={form.default_meta} onChange={e => setForm({ ...form, default_meta: e.target.value })} className="mt-1 h-9 text-sm" placeholder="0" data-testid="kpi-template-meta" /></div>
                </div>
                <div className="flex gap-2">
                  <Button size="sm" onClick={handleSave} data-testid="kpi-template-save">{editingId ? "Guardar Cambios" : "Crear"}</Button>
                  <Button size="sm" variant="outline" onClick={resetForm}>Cancelar</Button>
                </div>
              </div>
            )}
            <table className="w-full text-sm">
              <thead><tr className="bg-slate-50 border-b">
                <th className="px-4 py-2.5 text-left text-xs font-semibold text-slate-500">#</th>
                <th className="px-4 py-2.5 text-left text-xs font-semibold text-slate-500">Nombre</th>
                <th className="px-4 py-2.5 text-left text-xs font-semibold text-slate-500">Fuente</th>
                <th className="px-4 py-2.5 text-center text-xs font-semibold text-slate-500">Tipo</th>
                <th className="px-4 py-2.5 text-right text-xs font-semibold text-slate-500">Meta</th>
                <th className="px-4 py-2.5 text-right text-xs font-semibold text-slate-500">Acciones</th>
              </tr></thead>
              <tbody className="divide-y">
                {templates.length === 0 ? <tr><td colSpan={6} className="py-10 text-center text-slate-400">Sin objetivos. Clic "Agregar".</td></tr> :
                  templates.map((t, i) => {
                    const ds = dataSources.find(d => d.key === t.data_source);
                    return (
                      <tr key={t.id} className={`hover:bg-slate-50 ${editingId === t.id ? "bg-indigo-50" : ""}`} data-testid={`kpi-template-row-${t.id}`}>
                        <td className="px-4 py-2.5 text-slate-400 font-mono text-xs">{i + 1}</td>
                        <td className="px-4 py-2.5 font-medium text-slate-800">{t.name}</td>
                        <td className="px-4 py-2.5"><span className={`text-xs px-2 py-0.5 rounded-full ${t.data_source === "manual" ? "bg-slate-100 text-slate-600" : "bg-emerald-100 text-emerald-700"}`}>{ds?.label || t.data_source}</span></td>
                        <td className="px-4 py-2.5 text-center text-xs text-slate-600">{typeLabels[t.value_type]}</td>
                        <td className="px-4 py-2.5 text-right font-semibold text-slate-700">{fmtVal(t.default_meta, t.value_type)}</td>
                        <td className="px-4 py-2.5 text-right">
                          <div className="flex items-center justify-end gap-1.5">
                            <button onClick={() => handleStartEdit(t)} className="text-indigo-400 hover:text-indigo-600" data-testid={`kpi-edit-template-${t.id}`}><PencilSimple size={14} /></button>
                            <button onClick={() => handleDelete(t.id)} className="text-red-400 hover:text-red-600" data-testid={`kpi-delete-template-${t.id}`}><Trash size={14} /></button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}
